/**
 * Field-level comparison of two audit log snapshots (the full JSON of an entity stored with every audit record),
 * so the detail of an update can show which fields changed (RECEK-75765).
 */
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Keys that change on every update and carry no information for the user: the optimistic-lock version and the
 * FOLIO record metadata (created/updated date and user - the audit record itself shows when and who).
 */
var IGNORED_KEYS = new Set(["version", "metadata"]);
/**
 * Below the root, `id` is not compared: the modules re-create child rows (subjects, names, addresses, ...) with
 * new ids on every save, and a changed reference still shows through its value / description fields.
 */
var NESTED_ID_KEY = "id";
var isObject = function (value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};
/** A missing value, null and an empty object or array all mean "nothing there". */
var isMissing = function (value) {
    return value === undefined ||
        value === null ||
        (Array.isArray(value) && value.length === 0) ||
        (isObject(value) && Object.keys(value).length === 0);
};
/** ISO-8601 timestamp split into the part up to seconds, the fraction and the zone. */
var TIMESTAMP = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?(Z|[+-]\d{2}:\d{2})$/;
/** The instant of an ISO-8601 timestamp in epoch microseconds (rounded), or undefined for another string. */
var toEpochMicros = function (value) {
    var _a;
    var match = TIMESTAMP.exec(value);
    if (!match)
        return undefined;
    var seconds = Date.parse("".concat(match[1]).concat(match[3]));
    if (Number.isNaN(seconds))
        return undefined;
    var nanos = Number(((_a = match[2]) !== null && _a !== void 0 ? _a : "").padEnd(9, "0"));
    return seconds * 1000 + Math.round(nanos / 1000);
};
/**
 * The same instant written with a different precision - the snapshot taken at creation carries nanoseconds,
 * a later one read back from the database microseconds - is not a change.
 */
var isSameTimestamp = function (a, b) {
    var left = toEpochMicros(a);
    return left !== undefined && left === toEpochMicros(b);
};
var childPath = function (path, key) { return (path ? "".concat(path, ".").concat(key) : key); };
var isComparedKey = function (path, key) {
    return !IGNORED_KEYS.has(key) && !(path !== "" && key === NESTED_ID_KEY);
};
/** Canonical JSON of a value without the keys that are not compared, for content equality of array items. */
var canonical = function (value, path) {
    if (path === void 0) { path = "*"; }
    if (Array.isArray(value)) {
        return "[".concat(value.map(function (item) { return canonical(item, path); }).join(","), "]");
    }
    if (isObject(value)) {
        var keys = Object.keys(value)
            .filter(function (key) { return isComparedKey(path, key) && !isMissing(value[key]); })
            .sort();
        return "{".concat(keys
            .map(function (key) { return "".concat(JSON.stringify(key), ":").concat(canonical(value[key], path)); })
            .join(","), "}");
    }
    return JSON.stringify(value !== null && value !== void 0 ? value : null);
};
var changeOf = function (path, oldValue, newValue) {
    if (isMissing(oldValue)) {
        return { path: path, kind: "added", oldValue: undefined, newValue: newValue };
    }
    if (isMissing(newValue)) {
        return { path: path, kind: "removed", oldValue: oldValue, newValue: undefined };
    }
    return { path: path, kind: "changed", oldValue: oldValue, newValue: newValue };
};
var diffValues = function (path, oldValue, newValue, changes) {
    if (isMissing(oldValue) && isMissing(newValue))
        return;
    if (isObject(oldValue) && isObject(newValue)) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutual recursion
        diffObjects(path, oldValue, newValue, changes);
        return;
    }
    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutual recursion
        diffArrays(path, oldValue, newValue, changes);
        return;
    }
    if (typeof oldValue === "string" &&
        typeof newValue === "string" &&
        isSameTimestamp(oldValue, newValue)) {
        return;
    }
    if (oldValue !== newValue) {
        changes.push(changeOf(path, oldValue, newValue));
    }
};
function diffObjects(path, oldValue, newValue, changes) {
    var keys = __spreadArray(__spreadArray([], Object.keys(newValue), true), Object.keys(oldValue).filter(function (key) { return !(key in newValue); }), true);
    keys
        .filter(function (key) { return isComparedKey(path, key); })
        .forEach(function (key) {
        return diffValues(childPath(path, key), oldValue[key], newValue[key], changes);
    });
}
/**
 * Arrays of objects: (a) items with the same content (ids ignored) are unchanged wherever they moved,
 * (b) the rest are paired by a common id, (c) what is still left by position among the leftovers and compared
 * field by field, (d) the remainder is added / removed. A pair is reported under the item's new index, a
 * removed item under its old index. Other arrays are compared by index.
 */
function diffObjectArrays(path, oldValue, newValue, changes) {
    var oldLeft = oldValue.map(function (item, index) { return ({ item: item, index: index }); });
    var newLeft = [];
    // (a) unchanged content, as a multiset
    newValue.forEach(function (item, index) {
        var key = canonical(item, path);
        var match = oldLeft.findIndex(function (old) { return canonical(old.item, path) === key; });
        if (match >= 0) {
            oldLeft = oldLeft.filter(function (_, i) { return i !== match; });
        }
        else {
            newLeft.push({ item: item, index: index });
        }
    });
    var pairs = [];
    // (b) a common id
    newLeft = newLeft.filter(function (current) {
        var id = current.item.id;
        if (isMissing(id))
            return true;
        var match = oldLeft.findIndex(function (old) { return String(old.item.id) === String(id); });
        if (match < 0)
            return true;
        pairs.push({ old: oldLeft[match], current: current });
        oldLeft = oldLeft.filter(function (_, i) { return i !== match; });
        return false;
    });
    // (c) position among the leftovers
    var paired = Math.min(oldLeft.length, newLeft.length);
    for (var i = 0; i < paired; i++) {
        pairs.push({ old: oldLeft[i], current: newLeft[i] });
    }
    pairs
        .sort(function (a, b) { return a.current.index - b.current.index; })
        .forEach(function (_a) {
        var old = _a.old, current = _a.current;
        return diffValues("".concat(path, "[").concat(current.index, "]"), old.item, current.item, changes);
    });
    // (d) the remainder
    newLeft.slice(paired).forEach(function (_a) {
        var item = _a.item, index = _a.index;
        return changes.push(changeOf("".concat(path, "[").concat(index, "]"), undefined, item));
    });
    oldLeft.slice(paired).forEach(function (_a) {
        var item = _a.item, index = _a.index;
        return changes.push(changeOf("".concat(path, "[").concat(index, "]"), item, undefined));
    });
}
function diffArrays(path, oldValue, newValue, changes) {
    if (oldValue.every(isObject) && newValue.every(isObject)) {
        diffObjectArrays(path, oldValue, newValue, changes);
        return;
    }
    var length = Math.max(oldValue.length, newValue.length);
    for (var index = 0; index < length; index++) {
        diffValues("".concat(path, "[").concat(index, "]"), oldValue[index], newValue[index], changes);
    }
}
/**
 * Compares two parsed snapshots key by key. Arrays of objects are matched by content, then id, then position
 * (see diffObjectArrays), other arrays by index. `version` and `metadata` keys and ids below the root are not
 * compared, a missing value equals null or an empty object / array, and timestamps that are the same instant at
 * microsecond precision are equal.
 *
 * @param previous the previous snapshot of the entity
 * @param current the current snapshot of the entity
 * @returns the changed items in document order
 */
export var computeAuditDiff = function (previous, current) {
    var changes = [];
    diffValues("", previous, current, changes);
    return changes;
};
/**
 * Parses the JSON snapshot of an audit record.
 *
 * @param data the raw `data` of an audit record
 * @returns the parsed value, undefined when missing or not valid JSON
 */
export var parseAuditData = function (data) {
    if (!data)
        return undefined;
    try {
        return JSON.parse(data);
    }
    catch (_a) {
        return undefined;
    }
};
/**
 * Formats one side of a change for display.
 *
 * @param value the old or new value
 * @returns a dash for a missing value, a string as it is, anything else as JSON
 */
export var formatAuditValue = function (value) {
    if (isMissing(value))
        return "—";
    if (typeof value === "string")
        return value;
    return JSON.stringify(value);
};
